import { AxiosResponse } from "axios";
import axios from "axios";
import { doGet, encodeQueryData } from "../../Common/utils/https";
import { getRequestHeaders } from "../../Common/utils/https";
import { shopifyApiUrl } from "../firebase";

const baseUrl = shopifyApiUrl;

export interface ShopifyDiscountPartner {
  docId: string;
  shopDomain: string;
  name: string;
  description: string;
}

export interface ShopifyTransaction {
  docId: string;
  shopifyOrderId: string;
  discountCode: string;
  discountId: string;
  shopDomain: string;
  orderAmount: number;
  totalDiscountAmount: number;
  currency: string;
  test: boolean;
  createdDt: number;
  compensationGranted?: {
    internalCurrency: string;
    units: number;
  } | null;
  compensationGrantStatus?:
    | "pending"
    | "completed"
    | "failed"
    | null;
}

export interface ValidateDiscountPartnerResponse {
  success: boolean;
  exists: boolean;
  partner?: ShopifyDiscountPartner;
  message?: string;
  error?: string;
}

export interface GetShopifyTransactionsResponse {
  success: boolean;
  transactions: ShopifyTransaction[];
  count: number;
  error?: string;
}

/**
 * Validates if a discount partner exists for a given shop domain
 */
export const validateDiscountPartner = async (
  shop: string
): Promise<AxiosResponse<ValidateDiscountPartnerResponse>> => {
  const url = `${baseUrl}shopify/validate?${encodeQueryData({ shop })}`;
  const headers = await getRequestHeaders();
  return doGet(url, headers);
};

/**
 * Gets all transactions for a given shop domain
 * @param shop - Shopify shop domain (optional, can be derived from session token)
 * @param sessionToken - Shopify session token (JWT) fetched from App Bridge
 */
export const getShopifyTransactions = async (
  shop?: string,
  sessionToken?: string
): Promise<AxiosResponse<GetShopifyTransactionsResponse>> => {
  if (!sessionToken) {
    throw new Error("Session token is required for getShopifyTransactions");
  }

  const params: Record<string, string> = {};
  // Include shop in query only if provided (backend can derive from session token)
  if (shop) {
    params.shop = shop;
  }

  const url = `${baseUrl}shopify/transactions${Object.keys(params).length > 0 ? `?${encodeQueryData(params)}` : ""}`;

  // Add Authorization header with session token (required)
  const headers: Record<string, string> = await getRequestHeaders();
  headers.Authorization = `Bearer ${sessionToken}`;

  return axios.get(url, { headers });
};

export interface ShopifyInstallResponse {
  authorizationURL: string;
}

/**
 * Gets the Shopify OAuth authorization URL
 */
export const getShopifyInstallAuthorizationUrl = async (
  shop: string,
  host?: string
): Promise<AxiosResponse<ShopifyInstallResponse>> => {
  const params: Record<string, string> = { shop };
  if (host) {
    params.host = host;
  }
  const url = `${baseUrl}shopify/install?${encodeQueryData(params)}`;
  const headers = await getRequestHeaders();
  return doGet(url, headers);
};
